/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, {
  ReactNode,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import ReactGridLayout from "react-grid-layout";
import { useImmer } from "use-immer";
import AppPart from "./components/AppPart";
import {
  MAX_COLUMN,
  COLUMN_WIDTH,
  APP_PART_TYPES,
  TAGS,
  ROW_HEIGHT,
  MARGIN_X,
  COMPACT_TYPE,
  DEFAULT_MIN_W,
} from "./constants";
import { Props } from "./hook";
import { FormActionContext } from "./index";
import type { AppPartType } from "./types";
import { generateUuid } from "../utils";
import { defaultValueOptionDropDown } from "./components/AppPart/AppPartComponents/Dropdown";
import { useConfirmingLeave } from "@lhd/app/index";
import ResizingBar from "./components/ResizingBar";
import { StyledGird } from "./styled";
import AppCreateAsPreview from "../AppCreate";
import { CopyTypeAppStore } from "@lhd/constants";

export function capitalizeFirstLetter(string?: string) {
  // if (!string) return "";
  // return string.charAt(0).toUpperCase() + string.slice(1);
  const tag = TAGS.find((t) => t.app_part_type === string);
  return tag?.name.trim();
}
interface IProps extends Props {
  layout?: AppPartType[];
  parentAppPart?: AppPartType;
  extra?: ReactNode;
  tableLayout?: boolean;
  compactType?: "horizontal";
  rowHeight?: number;
  maxRows?: number;
  isPreview?: boolean;
  setIsPreview?: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging?: boolean;
  isNotAllowDrop?: boolean;
  appCopyType?: keyof typeof CopyTypeAppStore;
}
const AppFormEditor = React.forwardRef<AppPartType, IProps>(
  (
    {
      parentAppPart,
      layout = [],
      extra,
      tableLayout,
      compactType,
      rowHeight = 30,
      maxRows,
      isPreview,
      setIsPreview,
      isDragging,
      isNotAllowDrop,
      appCopyType,
    },
    selectedAppPartRef,
  ) => {
    const {
      contextState,
      setContextState,
      onLayoutChange,
      onHandleDropChange,
    } = useContext(FormActionContext);
    const { checkLeaveForm } = useConfirmingLeave();
    const [state, setState] = useImmer<{
      mounted: boolean;
    }>({
      mounted: false,
    });
    const [isReSizing, setIsReSizing] = useState(false);
    const [infoResizingBar, setInfoResizingBar] = useState<{
      left: number;
      isMatching: boolean;
    }>({ left: 0, isMatching: false });
    const onDrop = (
      _layout: ReactGridLayout.Layout[],
      layoutItem: ReactGridLayout.Layout,
      e: DragEvent,
    ) => {
      const draggingPartType = e.dataTransfer?.getData("text/plain");
      // @ts-ignore
      const droppedTag = selectedAppPartRef.current as AppPartType;
      if (droppedTag && droppedTag.app_part_type === draggingPartType) {
        let defaultValue = null;
        if (droppedTag?.app_part_type === APP_PART_TYPES.DROPDOWN) {
          defaultValue = defaultValueOptionDropDown?.[0]?.value;
        }

        let referencedFieldCode = droppedTag.referenced_field_code;
        if (
          !referencedFieldCode &&
          droppedTag?.app_part_type === APP_PART_TYPES.EMPLOYEE_SIGNATURE
        ) {
          for (const p of layout || []) {
            if (
              p.app_part_type === APP_PART_TYPES.WORKER_SELECTION &&
              p.field_code
            ) {
              referencedFieldCode = p.field_code;
              break;
            }
            if (p.children && Array.isArray(p.children)) {
              const foundChild = p.children.find(
                (c) =>
                  c.app_part_type === APP_PART_TYPES.WORKER_SELECTION &&
                  c.field_code,
              );
              if (foundChild) {
                referencedFieldCode = foundChild.field_code;
                break;
              }
            }
          }
        }

        const newData = {
          ...droppedTag,
          uuid: generateUuid(),
          id: undefined,
          default_value: defaultValue,
          referenced_field_code: referencedFieldCode,
          item_config_data: {
            i: generateUuid(),
            x: layoutItem.x,
            minW: droppedTag.item_config_data.minW ?? DEFAULT_MIN_W,
            maxH: droppedTag.item_config_data.maxH,
            minH: droppedTag.item_config_data.minH,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
            ...(referencedFieldCode && {
              referenced_field_code: referencedFieldCode,
            }),
          },
        } as AppPartType;

        checkLeaveForm();
        onHandleDropChange(newData, parentAppPart);
      }
    };
    const getCode = (item: AppPartType) => {
      const type = item.app_part_type;
      const listOfType = layout.filter((i) => i.app_part_type === type);
      const index = listOfType.findIndex((i) => i.uuid === item.uuid);
      return index;
    };

    const refContainerTableGird = useRef<HTMLDivElement>(null);
    const getInfoByElement = (element: Element) => {
      const style = element.getBoundingClientRect();

      return style;
    };

    useEffect(() => {
      const containerTableGird = document.querySelector(".inner ");
      if (isReSizing && containerTableGird) {
        const reactGridItems =
          containerTableGird.querySelectorAll(".react-grid-item");
        const reactGridItemResizing = containerTableGird.querySelector(
          ".react-grid-item.resizing",
        ) as HTMLElement;
        const observer = new ResizeObserver((reactGridItemResizing) => {
          const elementSizing = parentAppPart
            ? reactGridItemResizing[0].target
            : (containerTableGird.querySelector(
                ".react-resizable-hide",
              ) as HTMLElement);
          if (!elementSizing) return;
          const {
            width: widthSizing,
            left: leftSizing,
            top: topSizing,
            right: rightSizing,
          } = getInfoByElement(elementSizing);

          let isMatching = false;
          reactGridItems.forEach((reactGridItem: Element) => {
            const { left, top, right } = getInfoByElement(reactGridItem);
            if (
              rightSizing - right < 3 &&
              rightSizing - right > -2 &&
              !(left === leftSizing && top === topSizing)
            ) {
              reactGridItem.classList.add("matching");
              isMatching = true;
            } else {
              reactGridItem.classList.remove("matching");
            }
          });
          setInfoResizingBar({
            isMatching,
            left: rightSizing,
          });
        });

        if (reactGridItemResizing) {
          observer.observe(reactGridItemResizing);
        }

        return () => {
          if (reactGridItemResizing) {
            observer.unobserve(reactGridItemResizing);
          }
        };
      }
    }, [isReSizing, infoResizingBar.isMatching, isDragging]);

    useEffect(() => {
      setState((draft) => {
        draft.mounted = true;
      });
    }, [setState]);
    return isPreview ? (
      <AppCreateAsPreview
        layout={layout.map((item) => {
          const itemClone = { ...item };
          if (!itemClone.id) itemClone.id = itemClone.uuid;
          itemClone.label_text = itemClone.name || itemClone.label_text;
          return itemClone;
        })}
        isPreview={isPreview}
      />
    ) : (
      <StyledGird
        className={`${parentAppPart ? "children-inner" : "inner"}  ${
          infoResizingBar.isMatching ? "matching-react-grid-layout" : ""
        }`}
        ref={refContainerTableGird}
      >
        {isReSizing && (
          <ResizingBar
            key={infoResizingBar.left}
            container={refContainerTableGird.current}
            {...infoResizingBar}
          />
        )}
        {contextState.resetLayout && (
          <ReactGridLayout
            onDragStart={(a, b, c, d, e) => e.stopPropagation()}
            cols={MAX_COLUMN}
            className="layout"
            onLayoutChange={(layout) => {
              //if (parentAppPart) {
              onLayoutChange(layout, parentAppPart);
              //}
            }}
            layout={layout.map((item) => ({
              ...item.item_config_data,
              x: Number(item.item_config_data.x) || 0,
              y: Number(item.item_config_data.y) || 0,
              w: Number(item.item_config_data.w) || 1,
              h: Number(item.item_config_data.h) || 1,
            }))}
            onResizeStart={() => setIsReSizing(true)}
            onResizeStop={() => {
              setIsReSizing(false);
              setInfoResizingBar({ ...infoResizingBar, isMatching: false });
            }}
            width={MAX_COLUMN * COLUMN_WIDTH + COLUMN_WIDTH}
            compactType={tableLayout ? "horizontal" : COMPACT_TYPE}
            onDrop={onDrop}
            useCSSTransforms={state.mounted}
            preventCollision={tableLayout ? false : true}
            isDroppable={!contextState.isHoverParentItem && !isNotAllowDrop}
            isDraggable={!contextState.isDisableDragging}
            isBounded={true}
            rowHeight={ROW_HEIGHT}
            maxRows={maxRows}
            margin={tableLayout ? [0, 0] : [MARGIN_X, 0]} // Set gap to 0
            containerPadding={[0, 0]}
            style={{
              minHeight: "100%",
              minWidth: "100%",
              height: parentAppPart ? "100%" : "1800px",
            }}
            onDropDragOver={() => {
              return {
                minH: 1,
                // @ts-ignore
                h: selectedAppPartRef?.current?.item_config_data?.h ?? 2,
                w:
                  // @ts-ignore
                  selectedAppPartRef?.current?.item_config_data?.w ?? 12,
              };
            }}
          >
            {layout.map((layoutItem, index) => {
              const item = { ...layoutItem };
              const code = getCode(item);
              item.field_code = item.field_code
                ? item.field_code
                : `${
                    capitalizeFirstLetter(item.field_code) ||
                    capitalizeFirstLetter(item.app_part_type)
                  }${code ? "_" : ""}${code || ""}`;
              return (
                <div
                  className={`app-part-wrapper type-${item.app_part_type}`}
                  key={item.item_config_data.i}
                  style={{
                    zIndex: ["table", "field_group"].includes(
                      item.app_part_type,
                    )
                      ? 10
                      : 1,
                  }}
                  data-grid={item.item_config_data}
                  onDragOver={() => {
                    if (
                      ["table", "field_group"].includes(item.app_part_type) &&
                      !contextState.isHoverParentItem
                    ) {
                      setContextState((draft) => {
                        draft.isHoverParentItem = !draft.isHoverParentItem;
                      });
                    }
                  }}
                  onDragLeave={() => {
                    if (contextState.isHoverParentItem) {
                      setContextState((draft) => {
                        draft.isHoverParentItem = false;
                      });
                    }
                  }}
                >
                  <AppPart
                    appCopyType={appCopyType}
                    isDragging={isDragging}
                    appPart={item}
                    ref={selectedAppPartRef}
                    containerRef={refContainerTableGird}
                  />
                </div>
              );
            })}
          </ReactGridLayout>
        )}
        {extra}
      </StyledGird>
    );
  },
);
AppFormEditor.displayName = "AppFormEditor";

export default AppFormEditor;
